<template>
  <div>
    <div v-if="$slots['action-buttons'] || showSearch" class="flex mb-6 justify-between items-center flex-wrap">
      <div class="flex items-center">
        <slot name="action-buttons" />
        <search-input v-if="showSearch" v-model="computedSearch" class="ml-2" :tooltip="searchTooltip" :color="color" />
      </div>
      <div v-if="$slots['action-buttons-right']" class="flex items-center">
        <slot name="action-buttons-right" />
      </div>
    </div>
    <div v-if="loading && visibleData.length == 0" class="text-center">
      <d-loader />
    </div>
    <div v-else class="d-table" :class="{ 'is-loading': loading }">
      <d-table-mobile-sort v-if="mobileCards && hasSortablenewColumns" :current-sort-column="currentSortColumn" :is-asc="isAsc" :columns="computedColumns" :placeholder="mobileSortPlaceholder" @sort="(column) => sort(column)" />

      <table class="table" :class="tableClasses" :tabindex="!focusable ? false : 0" @keydown.self.prevent.up="pressedArrow(-1)" @keydown.self.prevent.down="pressedArrow(1)">
        <thead v-if="computedColumns.length">
          <tr>
            <th v-if="showDetailRowIcon" width="40px" />
            <th v-if="checkable" class="checkbox-cell">
              <d-checkbox :value="isAllChecked" :disabled="isAllUncheckable" @change="checkAll" />
            </th>
            <th v-for="(column, index) in visibleColumns" :key="index" :class="{'is-current-sort': isCurrentSortColumn(column),'is-sortable': column.sortable}" :style="{ width: column.width + 'px' }" @click.stop="sort(column)">
              <div class="th-wrap" :class="{ 'is-numeric': column.numeric, 'is-centered': column.centered }">
                <slot v-if="$slots.header" name="header" :column="column" :index="index" />
                <template v-else>
                  {{ column.label }}
                </template>

                <d-icon v-show="isCurrentSortColumn(column)" icon="arrow-up" :pack="iconPack" size="xs" class="mx-2" :class="{ 'is-desc': !isAsc }" />
              </div>
            </th>
            <template v-if="$slots.actionsColumn">
              <th />
            </template>
          </tr>
          <tr v-if="loading && visibleData.length">
            <th :colspan="visibleColumns.length + 1" class="p-0" style="border-width: 0;">
              <d-progress-bar indeterminate class="m-0" />
            </th>
          </tr>
        </thead>
        <tbody v-if="visibleData.length">
          <template v-for="(row, index) in visibleData" :key="customRowKey ? row[customRowKey] : index">
            <tr :class="[$props.rowClass(row, index), {'is-selected': row === selected,'is-checked': isRowChecked(row)}]" :draggable="draggable" @click="selectRow(row)" @dblclick="$emit('dblclick', row)" @contextmenu="$emit('contextmenu', row, $event)" @dragstart="handleDragStart($event, row, index)" @drop="handleDrop($event, row, index)" @dragover="handleDragOver($event, row, index)" @dragleave="handleDragLeave($event, row, index)">
              <td v-if="showDetailRowIcon" class="chevron-cell">
                <a v-if="$props.hasDetailedVisible(row)" role="button" @click.stop="toggleDetails(row)">
                  <d-icon icon="chevron-right" :pack="iconPack" :class="{'is-expanded': isVisibleDetailRow(row)}" />
                </a>
              </td>

              <td v-if="checkable" class="checkbox-cell">
                <d-checkbox :disabled="!$props.isRowCheckable(row) ? null : true" :value="$props.isRowChecked(row)" @change="checkRow(row)" @click.stop />
              </td>

              <slot v-if="$slots.default" :row="row" :index="index" />
              <template v-else>
                <d-table-column v-for="column in computedColumns" :key="column.field" v-bind="column" :row="row" internal />
              </template>
              <template v-if="$slots.actionsColumn">
                <d-table-column numeric internal>
                  <slot name="actionsColumn" :row="row" />
                </d-table-column>
              </template>
            </tr>

            <!-- Do not add `key` here (breaks details) -->
            <!-- eslint-disable-next-line -->
            <tr v-if="detailed && isVisibleDetailRow(row)" class="detail">
              <td :colspan="columnCount">
                <div class="detail-container">
                  <slot name="detail" :row="row" :index="index" />
                </div>
              </td>
            </tr>
          </template>
        </tbody>
        <tbody v-else>
          <tr class="is-empty">
            <td :colspan="columnCount">
              <slot v-if="$slots.empty" name="empty" />
              <div v-else class="text-center">
                No data was found.
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="$slots.footer !== undefined">
          <tr class="table-footer">
            <slot v-if="hasCustomFooterSlot()" name="footer" />
            <th v-else :colspan="columnCount">
              <slot name="footer" />
            </th>
          </tr>
        </tfoot>
      </table>

      <div v-if="(checkable && hasBottomLeftSlot()) || paginated" class="flex justify-end items-center">
        <div class="flex-initial">
          <slot name="bottom-left" />
        </div>

        <div class="flex-initial mt-3">
          <div v-if="paginated" class="level-item">
            <d-pagination :icon-pack="iconPack" :color="color" :total="newDataTotal" :per-page="perPage" :simple="paginationSimple" :size="paginationSize" :current="newCurrentPage" :aria-next-label="ariaNextLabel" :aria-previous-label="ariaPreviousLabel" :aria-page-label="ariaPageLabel" :aria-current-label="ariaCurrentLabel" @change="pageChanged" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { startCase, isEmpty} from 'lodash-es';
  import { defineComponent } from 'vue';
  import { getValueByPath, indexOf } from '../../../utils/helpers.js';
  import { tableComponentProps } from '../../../utils/mixins/tableMixin.js';
  import DCheckbox from '../DCheckbox/DCheckbox.vue';
  import DIcon from '../DIcon/DIcon.vue';
  import DLoader from '../DLoader/DLoader.vue';
  import DPagination from '../DPagination/DPagination.vue';
  import DTableMobileSort from './DTableMobileSort.vue';
  import DTableColumn from './DTableColumn.vue';
  import SearchInput from './SearchInput.vue';
  import DProgressBar from '../DProgressBar/DProgressBar.vue';

  /**
   * @displayName Table
   */
  export default defineComponent({
    name: 'DTable',
    status: 'prototype',
    release: '0.1.8',
    emits: ['update:search', 'check', 'check-all', 'update:checked-rows', 'click', 'select', 'update:selected', 'page-change', 'update:currentPage', 'details-close', 'details-open', 'update:openedDetailed', 'sort', 'update:checkedRows', 'dblclick', 'contextmenu', 'dragstart', 'drop', 'dragover', 'dragleave'],
    components: {
      DCheckbox,
      DIcon,
      DLoader,
      DPagination,
      DTableMobileSort,
      DTableColumn,
      SearchInput,
      DProgressBar,
    },
    props: {
      ...tableComponentProps,
    },
    data() {
      return {
        getValueByPath,
        visibleDetailRows: this.openedDetailed,
        newData: this.data,
        newDataTotal: this.backendPagination ? this.total : this.data.length,
        newCheckedRows: [...this.checkedRows],
        newColumns: [...this.columns],
        newCurrentPage: this.currentPage,
        currentSortColumn: {},
        isAsc: true,
        firstTimeSort: true, // Used by first time initSort
        _isTable: true, // Used by TableColumn
      };
    },
    computed: {
      /**
       * return if detailed row tabled
       * will be with chevron column & icon or not
       */
      showDetailRowIcon() {
        return this.detailed && this.showDetailIcon;
      },
      computedColumns: {
        get() {
          if (this.newColumns.length === 0 && this.visibleData.length > 0) {
            // If no columns are set, declare the object keys as column items.
            return Object.keys(this.data[0]).map((item) => ({
              field: item,
              label: startCase(item),
              numeric: typeof this.data[0][item] === 'number',
              sortable: true,
            }));
          }
          return this.newColumns;
        },
      },
      tableClasses() {
        return {
          'is-bordered': this.bordered,
          'is-striped': this.striped,
          'is-narrow': this.narrowed,
          'has-mobile-cards': this.mobileCards,
          'is-hoverable': (
            (this.hoverable || this.focusable)
            && this.visibleData.length
          ),
        };
      },
      /**
       * Splitted data based on the pagination.
       */
      visibleData() {
        const data = this.filteredItems;
        if (!this.paginated) return data;
        const currentPage = this.newCurrentPage;
        const { perPage } = this;
        if (data.length <= perPage) {
          return data;
        }
        const start = (currentPage - 1) * perPage;
        const end = parseInt(start, 10) + parseInt(perPage, 10);
        return data.slice(start, end);
      },
      visibleColumns() {
        if (!this.computedColumns) return this.computedColumns;
        return this.computedColumns.filter((column) => column.visible || column.visible === undefined);
      },
      /**
       * Check if all rows in the page are checked.
       */
      isAllChecked() {
        const validVisibleData = this.visibleData.filter(
          (row) => this.isRowCheckable(row),
        );
        if (validVisibleData.length === 0) return false;
        const isAllChecked = validVisibleData.some((currentVisibleRow) => indexOf(this.newCheckedRows, currentVisibleRow, this.customIsChecked) < 0);
        return !isAllChecked;
      },
      /**
       * Check if all rows in the page are checkable.
       */
      isAllUncheckable() {
        const validVisibleData = this.visibleData.filter(
          (row) => this.isRowCheckable(row),
        );
        return validVisibleData.length === 0 ? null : true;
      },
      /**
       * Check if has any sortable column.
       */
      hasSortablenewColumns() {
        return this.computedColumns.some((column) => column.sortable);
      },
      /**
       * Return total column count based if it's checkable or expanded
       */
      columnCount() {
        let count = this.computedColumns.length;
        count += this.checkable ? 1 : 0;
        count += this.detailed ? 1 : 0;
        return count;
      },

      computedSearch: {
        get() {
          return this.search;
        },
        set(value) {
          this.$emit('update:search', value);
        },
      },

      filteredItems() {
        return this.filteredItemsImpl();
      },
    },
    watch: {
      /**
       * When data prop change:
       *   1. Update internal value.
       *   2. Reset newColumns (thead), in case it's on a v-for loop.
       *   3. Sort again if it's not backend-sort.
       *   4. Set new total if it's not backend-paginated.
       */
      data(value) {
        // Save newColumns before resetting
        const { newColumns } = this;
        this.newColumns = [];
        this.newData = value;
        // Prevent table from being headless, data could change and created hook
        // on column might not trigger
        this.$nextTick(() => {
          if (!this.newColumns.length) this.newColumns = newColumns;
        });
        if (!this.backendSorting) {
          this.sort(this.currentSortColumn, true);
        }
        if (!this.backendPagination) {
          this.newDataTotal = value.length;
        }
      },

      /**
       * When Pagination total change, update internal total
       * only if it's backend-paginated.
       */
      total(newTotal) {
        if (!this.backendPagination) return;
        this.newDataTotal = newTotal;
      },
      /**
       * When checkedRows prop change, update internal value without
       * mutating original data.
       */
      checkedRows(rows) {
        this.newCheckedRows = [...rows];
      },
      columns(value) {
        this.newColumns = [...value];
      },
      newColumns() {
        this.checkSort();
      },
      /**
       * When the user wants to control the detailed rows via props.
       * Or wants to open the details of certain row with the router for example.
       */
      openedDetailed(expandedRows) {
        this.visibleDetailRows = expandedRows;
      },
      currentPage(newVal) {
        this.newCurrentPage = newVal;
      },
    },
    mounted() {
      this.checkPredefinedDetailedRows();
      this.checkSort();
    },
    methods: {
      filter(val, search) {
        return val != null && typeof val !== 'boolean' && val.toString().toLowerCase().indexOf(search) !== -1;
      },

      filteredItemsImpl() {
        const items = JSON.parse(JSON.stringify(this.newData));

        if (this.showSearch && !this.backendSearch) {
          if (this.search.trim() === '') return items;

          return items.filter((i) => (
            Object.keys(i).some((j) => this.filter(i[j], this.search.toLowerCase()))
          ));
        }

        return items;
      },

      /**
       * Sort an array by key without mutating original data.
       * Call the user sort function if it was passed.
       */
      sortBy(array, key, fn, isAsc) {
        let sorted = [];
        // Sorting without mutating original data
        if (fn && typeof fn === 'function') {
          sorted = [...array].sort((a, b) => fn(a, b, isAsc));
        } else {
          sorted = [...array].sort((a, b) => {
            // Get nested values from objects
            let newA = getValueByPath(a, key);
            let newB = getValueByPath(b, key);
            // sort boolean type
            if (typeof newA === 'boolean' && typeof newB === 'boolean') {
              return isAsc ? newA - newB : newB - newA;
            }
            if (!newA && newA !== 0) return 1;
            if (!newB && newB !== 0) return -1;
            if (newA === newB) return 0;
            newA = (typeof newA === 'string')
              ? newA.toUpperCase()
              : newA;
            newB = (typeof newB === 'string')
              ? newB.toUpperCase()
              : newB;
            return isAsc
              ? newA > newB ? 1 : -1
              : newA > newB ? -1 : 1;
          });
        }
        return sorted;
      },

      isCurrentSortColumn(column) {
        if (!column || !column.sortable || isEmpty(this.currentSortColumn)) { return false; }
        return ((column === this.currentSortColumn) || (column.field === this.currentSortColumn.field));
      },

      /**
       * Sort the column.
       * Toggle current direction on column if it's sortable
       * and not just updating the prop.
       */
      sort(column, updatingData = false) {
        if (!column || !column.sortable) return;

        if (!updatingData) {
          this.isAsc = this.isCurrentSortColumn(column)
            ? !this.isAsc
            : (this.defaultSortDirection.toLowerCase() !== 'desc');
        }

        if (!this.firstTimeSort) {
          this.$emit('sort', column.field, this.isAsc ? 'asc' : 'desc');
        }

        if (!this.backendSorting) {
          this.newData = this.sortBy(
            this.newData,
            column.field,
            column.customSort,
            this.isAsc,
          );
        }
        this.currentSortColumn = column;
      },
      /**
       * Check if the row is checked (is added to the array).
       */
      isRowChecked(row) {
        return indexOf(this.newCheckedRows, row, this.customIsChecked) >= 0;
      },
      /**
       * Remove a checked row from the array.
       */
      removeCheckedRow(row) {
        const index = indexOf(this.newCheckedRows, row, this.customIsChecked);
        if (index >= 0) {
          this.newCheckedRows.splice(index, 1);
        }
      },
      /**
       * Header checkbox click listener.
       * Add or remove all rows in current page.
       */
      checkAll() {
        const { isAllChecked } = this;
        this.visibleData.forEach((currentRow) => {
          this.removeCheckedRow(currentRow);
          if (!isAllChecked) {
            if (this.isRowCheckable(currentRow)) {
              this.newCheckedRows.push(currentRow);
            }
          }
        });
        this.$emit('check', this.newCheckedRows);
        this.$emit('check-all', this.newCheckedRows);
        // Emit checked rows to update user variable
        this.$emit('update:checkedRows', this.newCheckedRows);
      },
      /**
       * Row checkbox click listener.
       * Add or remove a single row.
       */
      checkRow(row) {
        if (!this.isRowChecked(row)) {
          this.newCheckedRows.push(row);
        } else {
          this.removeCheckedRow(row);
        }
        this.$emit('check', this.newCheckedRows, row);
        // Emit checked rows to update user variable
        this.$emit('update:checkedRows', this.newCheckedRows);
      },
      /**
       * Row click listener.
       * Emit all necessary events.
       */
      selectRow(row, index) {
        this.$emit('click', row);
        if (this.selected === row) return;
        // Emit new and old row
        this.$emit('select', row, this.selected);
        // Emit new row to update user variable
        this.$emit('update:selected', row);
      },
      /**
       * Paginator change listener.
       */
      pageChanged(page) {
        this.newCurrentPage = page > 0 ? page : 1;
        this.$emit('page-change', this.newCurrentPage);
        this.$emit('update:currentPage', this.newCurrentPage);
      },
      /**
       * Toggle to show/hide details slot
       */
      toggleDetails(obj) {
        const found = this.isVisibleDetailRow(obj);
        if (found) {
          this.closeDetailRow(obj);
          this.$emit('details-close', obj);
        } else {
          this.openDetailRow(obj);
          this.$emit('details-open', obj);
        }
        // Syncs the detailed rows with the parent component
        this.$emit('update:openedDetailed', this.visibleDetailRows);
      },
      openDetailRow(obj) {
        const index = this.handleDetailKey(obj);
        this.visibleDetailRows.push(index);
      },
      closeDetailRow(obj) {
        const index = this.handleDetailKey(obj);
        const i = this.visibleDetailRows.indexOf(index);
        this.visibleDetailRows.splice(i, 1);
      },
      isVisibleDetailRow(obj) {
        const index = this.handleDetailKey(obj);
        const result = this.visibleDetailRows.indexOf(index) >= 0;
        return result;
      },

      /**
       * When the detailKey is defined we use the object[detailKey] as index.
       * If not, use the object reference by default.
       */
      handleDetailKey(index) {
        const key = this.detailKey;
        return !key.length ? index : index[key];
      },

      checkPredefinedDetailedRows() {
        const defaultExpandedRowsDefined = this.openedDetailed.length > 0;
        if (defaultExpandedRowsDefined && !this.detailKey.length) {
          throw new Error('If you set a predefined opened-detailed, you must provide a unique key using the prop "detail-key"');
        }
      },

      /**
       * Call initSort only first time (For example async data).
       */
      checkSort() {
        if (this.computedColumns.length && this.firstTimeSort) {
          this.initSort();
          this.firstTimeSort = false;
        } else if (this.computedColumns.length) {
          if (this.currentSortColumn.field) {
            for (let i = 0; i < this.computedColumns.length; i++) {
              if (this.computedColumns[i].field === this.currentSortColumn.field) {
                this.currentSortColumn = this.computedColumns[i];
                break;
              }
            }
          }
        }
      },

      /**
       * Check if footer slot has custom content.
       */
      hasCustomFooterSlot() {
        if (this.$slots.footer().length > 1) return true;
        const { tag } = this.$slots.footer[0];
        if (tag !== 'th' && tag !== 'td') return false;
        return true;
      },

      /**
       * Check if bottom-left slot exists.
       */
      hasBottomLeftSlot() {
        return typeof this.$slots['bottom-left'] !== 'undefined';
      },

      /**
       * Table arrow keys listener, change selection.
       */
      pressedArrow(pos) {
        if (!this.visibleData.length) return;
        let index = this.visibleData.indexOf(this.selected) + pos;
        // Prevent from going up from first and down from last
        index = index < 0
          ? 0
          : index > this.visibleData.length - 1
            ? this.visibleData.length - 1
            : index;
        this.selectRow(this.visibleData[index]);
      },

      /**
       * Focus table element if has selected prop.
       */
      focus() {
        if (!this.focusable) return;
        this.$el.querySelector('table').focus();
      },

      /**
       * Initial sorted column based on the default-sort prop.
       */
      initSort() {
        if (!this.defaultSort) return;
        let sortField = '';
        let sortDirection = this.defaultSortDirection;
        if (Array.isArray(this.defaultSort)) {
          sortField = this.defaultSort[0];
          if (this.defaultSort[1]) {
            sortDirection = this.defaultSort[1];
          }
        } else {
          sortField = this.defaultSort;
        }
        this.newColumns.forEach((column) => {
          if (column.field === sortField) {
            this.isAsc = sortDirection.toLowerCase() !== 'desc';
            this.sort(column, true);
          }
        });
      },

      /**
       * Emits drag start event
       */
      handleDragStart(event, row, index) {
        this.$emit('dragstart', { event, row, index });
      },

      /**
       * Emits drop event
       */
      handleDrop(event, row, index) {
        this.$emit('drop', { event, row, index });
      },

      /**
       * Emits drag over event
       */
      handleDragOver(event, row, index) {
        this.$emit('dragover', { event, row, index });
      },

      /**
       * Emits drag leave event
       */
      handleDragLeave(event, row, index) {
        this.$emit('dragleave', { event, row, index });
      },
    },
  });
</script>